/**
 * PDP "Order Swatches" modal — self-contained.
 *
 * Renders a swatch grid grouped by fabric family with client-side material +
 * color filtering, multi-select tiles, a selected-swatches tray, and add-to-cart
 * that pushes every selection to cart as individual line items.
 *
 * Independent of the standalone Order Swatches collection page: all filtering and
 * state lives in this component, so it never touches Shopify faceted filters.
 *
 * Jira: MH-1279, MH-1280, MH-1281, MH-1282, MH-1283, MH-1284, MH-1289.
 */

/**
 * Parse a tile's `data-shadow-variants` ("id:qty;id:qty") into cart line items.
 * Mixed-material swatches use this to add their two component variants instead of
 * the swatch's own ($0/unavailable) variant. Returns [] when absent/malformed.
 */
function parseShadowVariants(raw) {
  if (!raw) return [];
  return raw
    .split(';')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [id, qty] = pair.split(':');
      return { id: Number(id), quantity: Number(qty) || 1 };
    })
    .filter((item) => Number.isFinite(item.id) && item.id > 0);
}

class SwatchModal extends HTMLElement {
  // Tray shows this many thumbnails before collapsing the rest behind a "+X" badge:
  // 6 on desktop, 5 on mobile.
  static VISIBLE_THUMBS_DESKTOP = 6;
  static VISIBLE_THUMBS_MOBILE = 5;

  constructor() {
    super();

    this.productId = this.dataset.productId;
    this.productName = this.dataset.productName;

    this.dialog = this.querySelector('dialog.swatch-modal__dialog');
    this.scrollRegion = this.querySelector('[data-swatch-scroll]');
    this.grid = this.querySelector('[data-swatch-grid]');
    this.noResults = this.querySelector('[data-swatch-no-results]');
    this.filtersEl = this.querySelector('[data-swatch-filters]');
    this.families = Array.from(this.querySelectorAll('.swatch-modal__family'));
    this.tiles = Array.from(this.querySelectorAll('[data-swatch-tile]'));

    // Tray
    this.tray = this.querySelector('[data-swatch-tray]');
    this.trayThumbs = this.querySelector('[data-swatch-tray-thumbs]');
    this.trayToggle = this.querySelector('[data-swatch-tray-toggle]');
    this.subtotalEl = this.querySelector('[data-swatch-subtotal]');
    this.addToCartButton = this.querySelector('[data-swatch-add-to-cart]');

    // Active filters: empty set === "All".
    this.activeFilters = { material: new Set(), color: new Set() };
    // Selection state, in chronological order. Keyed by swatch sample handle.
    this.selection = [];
    this.trayExpanded = false;
    // handle -> orderable variant id (resolved lazily at add-to-cart).
    this._variantCache = new Map();

    this.lastFocusedTrigger = null;
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.querySelectorAll('[data-swatch-modal-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close()),
    );

    // Backdrop click: a click whose target is the dialog itself (not its
    // content) lands on the ::backdrop — close on that.
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    // <dialog> handles Escape natively, firing a 'cancel' event. Let it close,
    // but route it through our close() so analytics + scroll-unlock still run.
    this.dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this.filtersEl?.addEventListener('click', (e) => {
      const pill = e.target.closest('.swatch-modal__pill');
      if (pill) this.handlePillClick(pill);
    });

    this.querySelector('[data-swatch-clear-filters]')?.addEventListener('click', () => this.clearFilters());

    this.grid?.addEventListener('click', (e) => {
      const button = e.target.closest('.swatch-modal__tile-button');
      if (button) this.toggleTile(button.closest('[data-swatch-tile]'));
    });

    this.addToCartButton?.addEventListener('click', () => this.addToCart());
    this.trayToggle?.addEventListener('click', () => this.toggleTray());
  }

  /* ----------------------------------------------------------------- open/close */

  /**
   * @param {Element} [trigger] the element that opened the modal. If it carries
   *   a data-active-swatch-handle (the customizer's selected swatch), the modal
   *   scrolls that swatch into view.
   */
  open(trigger) {
    this.lastFocusedTrigger = trigger || document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('swatch-modal-open');

    // showModal() puts the dialog in the browser top layer (above the
    // customizer's own upholstery <dialog>) and gives focus-trap + Esc for free.
    this.dialog.showModal();

    // Always open with filters cleared so the swatch the customer just selected in
    // the customizer is visible (preserved filters could otherwise hide it, leaving
    // them unable to add it). Selection/tray state is independent and persists.
    this.resetFilters();
    this.scrollToActiveSwatch(trigger);

    this.publish('swatch_modal_opened', {
      page_path: window.location.pathname,
      page_type: 'pdp',
      product_id: this.productId,
      product_name: this.productName,
      link_location_id: 'customizer',
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('swatch-modal-open');
    if (this.dialog.open) this.dialog.close();
    this.lastFocusedTrigger?.focus?.();

    this.publish('swatch_modal_closed', {
      page_path: window.location.pathname,
      page_type: 'pdp',
      product_id: this.productId,
      swatches_selected_count: this.selection.length,
      link_location_id: 'customizer',
    });
  }

  /**
   * Scroll the grid to the swatch the customer was last looking at in the
   * customizer (its sample handle). Falls back to the top of the grid when
   * there's no active swatch.
   */
  /**
   * Find a tile by the customizer's active-swatch handle.
   *
   * Mixed-material tiles carry the REAL swatch-kit handle (resolved from the
   * "Order Swatches - PDP" collection), while the customizer trigger still builds
   * its handle from {name}-{group}. These differ in connector words ("with" vs
   * "and", or neither) and a -mixed-materials vs -mixed-media suffix, so an
   * equality compare fails. Instead, drop the mixed-* suffix and the connector
   * tokens ("with"/"and") from both, then match when every trigger token is
   * present in the tile's tokens (token-subset). This mirrors the Liquid
   * tile-matcher in snippets/swatch-modal.liquid. Exact match wins first, so
   * non-mixed swatches are unaffected.
   */
  findTileByHandle(handle) {
    const exact = this.grid.querySelector(`[data-swatch-tile][data-swatch-handle="${handle}"]`);
    if (exact) return exact;

    const tokens = (h) =>
      (h || '')
        .replace(/-mixed-(materials|media)$/, '')
        .split('-')
        .filter((w) => w && w !== 'with' && w !== 'and');
    const target = tokens(handle);
    if (!target.length) return null;

    return (
      Array.from(this.grid.querySelectorAll('[data-swatch-tile]')).find((t) => {
        const tileTokens = tokens(t.dataset.swatchHandle);
        return target.every((w) => tileTokens.includes(w));
      }) || null
    );
  }

  scrollToActiveSwatch(trigger) {
    const handle = trigger?.dataset?.activeSwatchHandle;
    const tile = handle && this.findTileByHandle(handle);

    // Mark the swatch currently selected in the customizer with a "Currently
    // selected" caption under its name. This reflects the customizer selection
    // only — it never changes when swatches are added to / removed from the tray.
    this.markCurrentlySelected(tile);

    if (!tile) {
      this.scrollRegion.scrollTop = 0;
      return;
    }

    // Wait a frame so the dialog has laid out before measuring scroll offsets.
    // scrollIntoView centers the tile regardless of its offsetParent chain (the
    // nested family <section>s break a naive tile.offsetTop calculation); the
    // scroll region is the only scrollable ancestor, so only it moves.
    requestAnimationFrame(() => {
      tile.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
  }

  /**
   * Add a "Currently selected" caption under the active swatch's name (and remove
   * it from any previously-marked tile). Tied to the customizer selection only —
   * independent of the tray, so adding/removing swatches never changes it.
   */
  markCurrentlySelected(tile) {
    this.grid.querySelectorAll('.swatch-modal__tile--current').forEach((prev) => {
      prev.classList.remove('swatch-modal__tile--current');
      prev.querySelector('.swatch-modal__tile-current-label')?.remove();
    });

    if (!tile) return;

    tile.classList.add('swatch-modal__tile--current');
    const nameEl = tile.querySelector('.swatch-modal__tile-name');
    if (nameEl && !tile.querySelector('.swatch-modal__tile-current-label')) {
      // Wrap the name in a column group (reuse it if we already wrapped this tile
      // on a prior open) so the caption sits directly under the name, leaving the
      // +/- toggle in place on the right of the row.
      let group = nameEl.closest('.swatch-modal__tile-name-group');
      if (!group) {
        group = document.createElement('span');
        group.className = 'swatch-modal__tile-name-group';
        nameEl.insertAdjacentElement('beforebegin', group);
        group.appendChild(nameEl);
      }

      const label = document.createElement('span');
      label.className = 'swatch-modal__tile-current-label body-3';
      label.textContent = 'Currently selected';
      group.appendChild(label);
    }
  }

  /* -------------------------------------------------------------------- filters */

  handlePillClick(pill) {
    const group = pill.closest('[data-filter-group]')?.dataset.filterGroup;
    if (!group) return;
    const value = pill.dataset.filterValue;
    const set = this.activeFilters[group];
    const row = pill.closest('[data-filter-group]');
    const allPill = row.querySelector('[data-filter-value="all"]');

    if (value === 'all') {
      set.clear();
    } else {
      // Toggle this value; pills are multi-select and additive.
      if (set.has(value)) set.delete(value);
      else set.add(value);
    }

    // Reflect pressed state. "All" is active only when no specific value is set.
    row.querySelectorAll('.swatch-modal__pill').forEach((p) => {
      const v = p.dataset.filterValue;
      const on = v === 'all' ? set.size === 0 : set.has(v);
      p.classList.toggle('is-selected', on);
      p.setAttribute('aria-pressed', String(on));
    });
    if (allPill) {
      const allOn = set.size === 0;
      allPill.classList.toggle('is-selected', allOn);
      allPill.setAttribute('aria-pressed', String(allOn));
    }

    this.applyFilters();

    this.publish('swatch_modal_filtered', {
      filter_type: group,
      filter_value: value,
      page_path: window.location.pathname,
      page_type: 'pdp',
      product_id: this.productId,
    });
  }

  /** Reset both filter rows to "All" and re-show every swatch. No analytics. */
  resetFilters() {
    this.activeFilters.material.clear();
    this.activeFilters.color.clear();
    this.filtersEl.querySelectorAll('.swatch-modal__pill').forEach((p) => {
      const isAll = p.dataset.filterValue === 'all';
      p.classList.toggle('is-selected', isAll);
      p.setAttribute('aria-pressed', String(isAll));
    });
    this.applyFilters();
  }

  clearFilters() {
    this.resetFilters();

    this.publish('swatch_filter_cleared', {
      product_id: this.productId,
      page_path: window.location.pathname,
    });
  }

  tileMatches(tile) {
    const mats = this.activeFilters.material;
    const cols = this.activeFilters.color;
    const materialOk = mats.size === 0 || mats.has(tile.dataset.material);
    const colorOk = cols.size === 0 || cols.has(tile.dataset.color);
    return materialOk && colorOk; // additive: material AND color
  }

  applyFilters() {
    let visibleCount = 0;

    this.tiles.forEach((tile) => {
      const match = this.tileMatches(tile);
      tile.hidden = !match;
      if (match) visibleCount += 1;
    });

    // Hide families with zero visible tiles; keep heading+desc otherwise.
    this.families.forEach((family) => {
      const hasVisible = family.querySelector('[data-swatch-tile]:not([hidden])');
      family.hidden = !hasVisible;
    });

    const empty = visibleCount === 0;
    this.noResults.hidden = !empty;
    this.grid.hidden = empty;

    if (empty) {
      this.publish('swatch_filter_no_results', {
        product_id: this.productId,
        page_path: window.location.pathname,
      });
    }
  }

  /* ------------------------------------------------------------------ selection */

  toggleTile(tile) {
    if (!tile) return;
    // Out-of-stock swatches can't be added (button is also disabled server-side).
    if (tile.dataset.oos === 'true') return;
    // Swatches are keyed by their sample handle (stable, unique). The orderable
    // variant id is resolved lazily at add-to-cart time (see resolveVariantId).
    const handle = tile.dataset.swatchHandle;
    const existing = this.selection.findIndex((s) => s.handle === handle);

    if (existing > -1) {
      this.selection.splice(existing, 1);
      this.setTileSelected(tile, false);
      this.publish('swatch_removed', this.tileEventData(tile));
    } else {
      this.selection.push({
        handle,
        title: tile.dataset.title,
        color_name: tile.dataset.colorName,
        family: tile.dataset.family,
        image: tile.dataset.image,
        product_id: tile.dataset.productId,
        // Price in cents; upholstery tiles omit data-price (free), wood is $25.
        price: parseInt(tile.dataset.price, 10) || 0,
        // Mixed-material swatches add their shadow variants instead of their own.
        // Format: "id:qty;id:qty". Absent for normal swatches.
        shadowVariants: tile.dataset.shadowVariants || '',
      });
      this.setTileSelected(tile, true);
      this.publish('swatch_added', this.tileEventData(tile));
    }

    this.renderTray();
  }

  setTileSelected(tile, selected) {
    tile.classList.toggle('is-selected', selected);
    const btn = tile.querySelector('.swatch-modal__tile-button');
    btn?.setAttribute('aria-pressed', String(selected));
  }

  tileEventData(tile) {
    // Mirrors the `swatch_added` / `swatch_removed` payload from the standalone
    // Order Swatches page (swatch-grid.js) so the web pixel's existing
    // subscriptions track them identically. The orderable variant is resolved
    // lazily at add-to-cart, so variant_id/sku aren't known here — the native
    // `product_added_to_cart` event carries them on cart add.
    return {
      title: tile.dataset.title,
      option_name: tile.dataset.colorName,
      category: tile.dataset.family,
      product_id: tile.dataset.productId,
      variant_id: null,
      sku: null,
      handle: tile.dataset.swatchHandle,
      price: { amount: (parseInt(tile.dataset.price, 10) || 0) / 100, currency: window.Shopify?.currency?.active },
      image: tile.dataset.image,
      page_type: 'pdp',
      page_path: window.location.pathname,
    };
  }

  /* ----------------------------------------------------------------------- tray */

  renderTray() {
    const count = this.selection.length;

    if (count === 0) {
      this.tray.hidden = true;
      this.trayExpanded = false;
      return;
    }
    this.tray.hidden = false;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const limit = isMobile ? SwatchModal.VISIBLE_THUMBS_MOBILE : SwatchModal.VISIBLE_THUMBS_DESKTOP;
    const overflow = count - limit;
    const showAll = this.trayExpanded || overflow <= 0;

    const visible = showAll ? this.selection : this.selection.slice(0, limit);
    this.trayThumbs.innerHTML = visible
      .map(
        (s) => `
        <button type="button" class="swatch-modal__tray-thumb" data-handle="${s.handle}" aria-label="Remove ${this.escape(
          s.color_name,
        )} from selection">
          <img src="${s.image}" alt="${this.escape(s.color_name)}" width="48" height="48">
          <span class="swatch-modal__tray-thumb-remove" aria-hidden="true">×</span>
        </button>`,
      )
      .join('');

    this.trayThumbs.querySelectorAll('.swatch-modal__tray-thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => this.removeByHandle(thumb.dataset.handle));
    });

    // Overflow toggle ("+X" / "Less").
    if (overflow > 0) {
      this.trayToggle.hidden = false;
      this.trayToggle.textContent = this.trayExpanded
        ? this.trayToggle.dataset.closeLabel
        : this.trayToggle.dataset.openTemplate.replace('{count}', overflow);
      this.trayToggle.setAttribute('aria-expanded', String(this.trayExpanded));
    } else {
      this.trayToggle.hidden = true;
      this.trayExpanded = false;
    }

    // Show the running subtotal when any selected swatch is paid (e.g. wood
    // finishes are $25); free upholstery samples keep it hidden.
    if (this.subtotalEl) {
      const cents = this.selection.reduce((sum, s) => sum + (s.price || 0), 0);
      this.subtotalEl.textContent = cents > 0 ? ` · ${this.formatMoney(cents)}` : '';
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  toggleTray() {
    this.trayExpanded = !this.trayExpanded;
    this.renderTray();
  }

  removeByHandle(handle) {
    const idx = this.selection.findIndex((s) => s.handle === handle);
    if (idx === -1) return;
    this.selection.splice(idx, 1);

    const tile = this.grid.querySelector(`[data-swatch-tile][data-swatch-handle="${handle}"]`);
    if (tile) {
      this.setTileSelected(tile, false);
      this.publish('swatch_removed', this.tileEventData(tile));
    }
    this.renderTray();
  }

  /* -------------------------------------------------------------------- add to cart */

  /**
   * Resolve a swatch sample handle to its orderable variant id via the product
   * JSON endpoint. We can't resolve all ~80 swatches up front in Liquid (the
   * `all_products` lookup is capped at ~20 per page), so resolution is lazy and
   * only happens for the swatches a customer actually selects. Results are cached.
   */
  async resolveVariantId(handle) {
    if (this._variantCache.has(handle)) return this._variantCache.get(handle);
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
    if (!res.ok) throw new Error(`Could not resolve swatch "${handle}" (${res.status})`);
    const product = await res.json();
    const variantId = product?.variants?.[0]?.id;
    if (!variantId) throw new Error(`No variant for swatch "${handle}"`);
    this._variantCache.set(handle, variantId);
    return variantId;
  }

  async addToCart() {
    if (!this.selection.length || this.addToCartButton.disabled) return;
    this.addToCartButton.disabled = true;
    this.addToCartButton.classList.add('is-loading');

    try {
      // Build line items. A mixed-material swatch (one tile = two physical swatches)
      // carries shadow variants ("id:qty;id:qty") and adds those instead of its own
      // ($0/unavailable) variant. Normal swatches resolve their own variant lazily.
      const itemLists = await Promise.all(
        this.selection.map(async (s) => {
          const shadow = parseShadowVariants(s.shadowVariants);
          if (shadow.length) return shadow;
          return [{ id: await this.resolveVariantId(s.handle), quantity: 1 }];
        }),
      );
      const items = itemLists.flat();
      // Shopify.addToCart fires Shopify's native `product_added_to_cart` event
      // (one per line item), which the web pixel already tracks — so we don't
      // publish a separate cart event here.
      await window.Shopify.addToCart(items);

      this.resetSelection();
      this.close();
    } catch (err) {
      console.error('[swatch-modal] add to cart failed', err);
    } finally {
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('is-loading');
    }
  }

  resetSelection() {
    // Clear selection, tray, and reset filters to default "All".
    this.grid.querySelectorAll('[data-swatch-tile].is-selected').forEach((tile) => this.setTileSelected(tile, false));
    this.selection = [];
    this.trayExpanded = false;
    this.renderTray();
    this.activeFilters.material.clear();
    this.activeFilters.color.clear();
    this.filtersEl?.querySelectorAll('.swatch-modal__pill').forEach((p) => {
      const isAll = p.dataset.filterValue === 'all';
      p.classList.toggle('is-selected', isAll);
      p.setAttribute('aria-pressed', String(isAll));
    });
    this.applyFilters();
  }

  /* -------------------------------------------------------------------- helpers */

  publish(event, payload) {
    try {
      window.Shopify?.analytics?.publish?.(event, payload);
    } catch (e) {
      /* analytics is best-effort */
    }
  }

  escape(str = '') {
    return String(str).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  }
}

customElements.define('swatch-modal', SwatchModal);

/**
 * Trigger element / delegation. Any element with [data-swatch-modal-trigger]
 * opens the swatch modal in the same section (falling back to the first modal
 * on the page). Keeps the element's href as a no-JS fallback.
 */
class SwatchModalTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const modal = this.resolveModal();
      if (!modal) return; // let the href fallback navigate
      e.preventDefault();
      modal.open(this);
    });
  }

  resolveModal() {
    const target = this.dataset.target;
    if (target) return document.getElementById(target);
    return this.closest('.shopify-section')?.querySelector('swatch-modal') || document.querySelector('swatch-modal');
  }
}

if (!customElements.get('swatch-modal-trigger')) {
  customElements.define('swatch-modal-trigger', SwatchModalTrigger);
}

/**
 * Progressive enhancement for the existing "Order Swatches" CTAs.
 *
 * The customizer's "Order Swatches" entry points are anchors that link to the
 * standalone swatch collection (e.g. `/collections/order-swatches` or
 * `/collections/materials-archive`), or carry the `product__link--swatches`
 * class. When a swatch modal is present on the page we intercept the click and
 * open the modal instead; the href stays intact as a no-JS / no-modal fallback.
 *
 * Scoped to the product customizer so global nav "Swatches" links are untouched.
 */
const SWATCH_TRIGGER_SELECTOR = [
  '[data-swatch-modal-trigger]',
  'a.product__link--swatches',
  '.active-swatch__cta[href]',
  'variant-picker-select a[href*="order-swatches"]',
  'variant-picker-select a[href*="materials-archive"]',
  '.variant-picker-select__active-swatch a[href*="order-swatches"]',
  '.variant-picker-select__active-swatch a[href*="materials-archive"]',
].join(', ');

document.addEventListener('click', (e) => {
  const link = e.target.closest(SWATCH_TRIGGER_SELECTOR);
  if (!link) return;
  if (link.tagName === 'SWATCH-MODAL-TRIGGER') return; // handled by the element itself

  const section = link.closest('.shopify-section');
  const modal = (section && section.querySelector('swatch-modal')) || document.querySelector('swatch-modal');
  if (!modal) return; // no modal on this page → let the link navigate

  e.preventDefault();
  modal.open(link);
});
